import { JOB_PURPOSE_LABELS, Job, JobList, QueueTimeline, formatTime } from '@deizi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from './api';
import { formatHm } from './numbers';
import { productsKey } from './products-api';

export const jobsKey = ['jobs'] as const;

/** Queued and printing jobs; refreshes every minute so countdowns and problems stay current. */
export const useQueue = () =>
  useQuery({
    queryKey: [...jobsKey, 'active'],
    queryFn: () => api.get('/jobs', JobList),
    refetchInterval: 60_000,
  });

/** One run by id (a link from an order to an older run). */
export const useJob = (id: string | null) =>
  useQuery({
    queryKey: [...jobsKey, 'one', id],
    queryFn: () => api.get(`/jobs/${id}`, Job),
    enabled: !!id,
  });

/** Failed prints of a month ("Ver impressões com falha"). */
export const useFailedJobs = (month: string | null) =>
  useQuery({
    queryKey: [...jobsKey, 'failed', month],
    queryFn: () => api.get(`/jobs?status=failed&month=${month}`, JobList),
    enabled: !!month,
  });

/** Prints outside orders of a month ("Impressões fora de pedido", from the month result). */
export const useOwnPrints = (month: string | null) =>
  useQuery({
    queryKey: [...jobsKey, 'own', month],
    queryFn: () => api.get(`/jobs?status=done,failed&month=${month}&purpose=any`, JobList),
    enabled: !!month,
  });

/** Jobs that ended since the start of today (São Paulo). */
export function useToday() {
  const [since] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start.toISOString();
  });
  return useQuery({
    queryKey: [...jobsKey, 'today', since.slice(0, 10)],
    queryFn: () =>
      api.get(`/jobs?status=done,failed&finishedSince=${encodeURIComponent(since)}`, JobList),
  });
}

export const useTimeline = () =>
  useQuery({
    queryKey: [...jobsKey, 'timeline'],
    queryFn: () => api.get('/queue/timeline', QueueTimeline),
    refetchInterval: 60_000,
  });

/**
 * Jobs, spools, stock, printers and quotes' lead times all move with the queue. Resolves once the
 * jobs are refetched, so a dialog can close on the new state (no stale card to tap).
 */
export function useInvalidateQueue() {
  const qc = useQueryClient();
  return async () => {
    for (const key of [
      ['spools'],
      ['inventory'],
      ['filaments'],
      ['supplies'],
      ['printers'],
      productsKey,
    ]) {
      void qc.invalidateQueries({ queryKey: key });
    }
    await qc.invalidateQueries({ queryKey: jobsKey });
  };
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** "termina hoje às 14h30" / "termina quinta, 08/10, às 9h12" */
export function whenText(iso: string, prefix = ''): string {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const time = formatTime(d);
  if (sameDay(d, today)) return `${prefix}hoje às ${time}`;
  if (sameDay(d, tomorrow)) return `${prefix}amanhã às ${time}`;
  const day = d
    .toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'America/Sao_Paulo' })
    .split('-')[0];
  const dm = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
  return `${prefix}${day}, ${dm}, às ${time}`;
}

/** "P-2026-0122 (3) + estoque (9)", "pronta entrega". */
export function destText(j: Job): string {
  if (j.purpose) return JOB_PURPOSE_LABELS[j.purpose].toLowerCase();
  if (!j.orders.length) return j.stockUnits > 0 ? 'pronta entrega' : 'pedido';
  const orders = j.orders.map((o) => `${o.number} (${o.units})`).join(' + ');
  return j.stockUnits > 0 ? `${orders} + estoque (${j.stockUnits})` : orders;
}

/** "P-2026-0122 · 3 un. + pronta entrega · 9 un." */
export function destLongText(j: Job): string {
  if (j.purpose) return `${JOB_PURPOSE_LABELS[j.purpose]} (fora de pedido)`;
  const parts = j.orders.map((o) => `${o.number} · ${o.units} un.`);
  if (j.stockUnits > 0) parts.push(`pronta entrega · ${j.stockUnits} un.`);
  return parts.join(' + ') || 'pedido';
}

/** "J-0214 · pronta entrega · 3h55 · 108 g" */
export function jobMeta(j: Job): string {
  const dest = destText(j);
  const redo = j.redoOf ? `refazer · falhou ${j.redoOf.failPct ?? 0}%` : null;
  return [j.number, redo ?? dest, formatHm(j.estSeconds), `${Math.round(j.estGrams)} g`].join(
    ' · ',
  );
}

/** Seconds left of a printing job. */
export const secondsLeft = (j: Job, now: number) =>
  j.expectedEndAt ? Math.max(0, (new Date(j.expectedEndAt).getTime() - now) / 1000) : 0;
